import React, { useState } from 'react';

const ArtemisUseCases = () => {
  const [selectedCategory, setSelectedCategory] = useState('developer');

  const useCaseCategories = {
    developer: {
      title: '👨‍💻 Individual Developer',
      icon: '💻',
      color: '#4ECDC4',
      repositoryAnalysis: [
        { title: 'Real-time Performance Scoring', desc: 'See performance impact as you type in IDE' },
        { title: 'Algorithm Complexity Detection', desc: 'Identify O(n²) patterns and get O(n) alternatives' },
        { title: 'Memory Leak Prevention', desc: 'Catch memory issues before commit' },
        { title: 'Security Vulnerability Scanning', desc: 'Real-time security analysis with fixes' },
        { title: 'Code Quality Metrics', desc: 'Instant readability and maintainability scores' },
        { title: 'Best Practice Enforcement', desc: 'Automated suggestions for design patterns' },
        { title: 'Technical Debt Tracking', desc: 'See debt accumulation in real-time' },
        { title: 'Performance Regression Alerts', desc: 'Warning when changes slow down code' },
        { title: 'Cost Impact Preview', desc: 'See cloud cost implications of code changes' },
        { title: 'Dependency Analysis', desc: 'Impact of adding/updating dependencies' }
      ],
      artemisIntelligence: [
        { title: 'Multi-Variation Generation', desc: 'Generate 5+ optimized versions of your code' },
        { title: 'Context-Aware Refactoring', desc: 'AI understands business logic during refactor' },
        { title: 'Performance Evolution', desc: 'Evolve code through genetic algorithms' },
        { title: 'Smart Bug Fixing', desc: 'AI fixes bugs while maintaining functionality' },
        { title: 'Code Completion Plus', desc: 'Complete functions with performance in mind' },
        { title: 'Test Generation', desc: 'Create comprehensive test suites automatically' },
        { title: 'Documentation Generation', desc: 'AI writes docs that explain the "why"' },
        { title: 'Cross-Language Translation', desc: 'Convert code between languages optimally' },
        { title: 'API Client Generation', desc: 'Generate type-safe, efficient API clients' },
        { title: 'Error Handling Evolution', desc: 'Evolve robust error handling patterns' }
      ]
    },
    dataScientist: {
      title: '📊 Data Scientist / ML Engineer',
      icon: '🧬',
      color: '#9370DB',
      repositoryAnalysis: [
        { title: 'ML Pipeline Performance Analysis', desc: 'Find bottlenecks in training pipelines' },
        { title: 'GPU Utilization Scoring', desc: 'Identify underutilized GPU resources' },
        { title: 'Data Loading Optimization', desc: 'Score data preprocessing efficiency' },
        { title: 'Model Serving Analysis', desc: 'Evaluate inference pipeline performance' },
        { title: 'Memory Usage Profiling', desc: 'Track memory consumption across pipeline' },
        { title: 'Batch Processing Efficiency', desc: 'Optimize batch sizes and processing' },
        { title: 'Feature Pipeline Analysis', desc: 'Score feature engineering efficiency' },
        { title: 'Training Cost Estimation', desc: 'Predict costs for different approaches' },
        { title: 'Model Complexity Scoring', desc: 'Balance accuracy vs computational cost' },
        { title: 'Data Leakage Detection', desc: 'Identify potential data leakage issues' }
      ],
      artemisIntelligence: [
        { title: 'Feature Engineering Evolution', desc: 'Evolve optimal features using genetic algorithms' },
        { title: 'Hyperparameter Optimization', desc: 'Multi-objective optimization for best params' },
        { title: 'Model Architecture Search', desc: 'Evolve neural network architectures' },
        { title: 'Pipeline Optimization', desc: 'Generate efficient end-to-end ML pipelines' },
        { title: 'AutoML Enhancement', desc: 'Improve AutoML with domain knowledge' },
        { title: 'Experiment Tracking Integration', desc: 'Auto-generate experiment variations' },
        { title: 'Model Compression', desc: 'Evolve smaller models with same accuracy' },
        { title: 'Data Augmentation Generation', desc: 'Create novel augmentation strategies' },
        { title: 'Ensemble Creation', desc: 'Evolve optimal model ensembles' },
        { title: 'Production Optimization', desc: 'Convert research code to production-ready' }
      ]
    },
    teamLead: {
      title: '👨‍💼 Team Lead / Engineering Manager',
      icon: '📈',
      color: '#DDA0DD',
      repositoryAnalysis: [
        { title: 'Codebase Health Dashboard', desc: 'Overall quality metrics across all repos' },
        { title: 'Team Performance Metrics', desc: 'Track code quality by team/developer' },
        { title: 'Technical Debt Heatmap', desc: 'Visualize debt concentration areas' },
        { title: 'Onboarding Complexity Analysis', desc: 'Find hard-to-understand code for new hires' },
        { title: 'Architecture Compliance', desc: 'Check adherence to architectural patterns' },
        { title: 'Security Posture Tracking', desc: 'Monitor security score trends over time' },
        { title: 'Performance Regression Tracking', desc: 'Historical performance analysis' },
        { title: 'Code Review Efficiency', desc: 'Analyze PR quality and review times' },
        { title: 'Knowledge Distribution', desc: 'Identify knowledge silos and bus factors' },
        { title: 'Sprint Quality Reports', desc: 'Code quality metrics per sprint' }
      ],
      artemisIntelligence: [
        { title: 'Custom Audit Queries', desc: 'Ask any question about your codebase' },
        { title: 'Refactoring Roadmaps', desc: 'AI-generated modernization plans' },
        { title: 'Team Skill Gap Analysis', desc: 'Identify training needs from code patterns' },
        { title: 'Architecture Evolution', desc: 'Generate migration strategies' },
        { title: 'Cost Optimization Plans', desc: 'AI identifies cost-saving opportunities' },
        { title: 'Risk Assessment', desc: 'Predict potential failure points' },
        { title: 'Compliance Automation', desc: 'Generate compliance reports' },
        { title: 'Knowledge Base Creation', desc: 'Auto-generate team documentation' },
        { title: 'Best Practice Extraction', desc: 'Learn patterns from your best code' },
        { title: 'Technical Interview Questions', desc: 'Generate questions based on codebase' }
      ]
    },
    devops: {
      title: '🚀 DevOps / Platform Engineer',
      icon: '⚙️',
      color: '#FFA726',
      repositoryAnalysis: [
        { title: 'CI/CD Pipeline Analysis', desc: 'Score pipeline efficiency and bottlenecks' },
        { title: 'Infrastructure Code Review', desc: 'Analyze Terraform/K8s configurations' },
        { title: 'Container Optimization', desc: 'Score Docker images for size/security' },
        { title: 'Deployment Risk Scoring', desc: 'Predict deployment failure probability' },
        { title: 'Resource Utilization', desc: 'Analyze cloud resource efficiency' },
        { title: 'Build Time Analysis', desc: 'Identify slow build steps' },
        { title: 'Dependency Security Scan', desc: 'Check all dependencies for vulnerabilities' },
        { title: 'Configuration Drift Detection', desc: 'Find inconsistencies across environments' },
        { title: 'Monitoring Coverage', desc: 'Identify unmonitored critical paths' },
        { title: 'Disaster Recovery Readiness', desc: 'Score backup and recovery procedures' }
      ],
      artemisIntelligence: [
        { title: 'Pipeline Generation', desc: 'Create optimized CI/CD pipelines' },
        { title: 'Infrastructure Evolution', desc: 'Evolve IaC for better performance/cost' },
        { title: 'Auto-scaling Rules', desc: 'Generate intelligent scaling policies' },
        { title: 'Monitoring Strategy', desc: 'Create comprehensive monitoring setup' },
        { title: 'Security Hardening', desc: 'Evolve security configurations' },
        { title: 'Cost Optimization', desc: 'Generate cost-efficient architectures' },
        { title: 'Deployment Strategies', desc: 'Create blue-green, canary deployments' },
        { title: 'Incident Response Automation', desc: 'Generate runbooks and automations' },
        { title: 'Performance Tuning', desc: 'Optimize system configurations' },
        { title: 'Multi-Cloud Migration', desc: 'Generate cloud migration strategies' }
      ]
    },
    architect: {
      title: '🏗️ Software Architect / Principal Engineer',
      icon: '🎨',
      color: '#96CEB4',
      repositoryAnalysis: [
        { title: 'Architecture Debt Analysis', desc: 'Identify architectural anti-patterns' },
        { title: 'Microservice Boundaries', desc: 'Analyze service coupling and cohesion' },
        { title: 'API Design Scoring', desc: 'Rate API consistency and usability' },
        { title: 'Database Query Analysis', desc: 'Find N+1 queries and inefficiencies' },
        { title: 'Scalability Assessment', desc: 'Predict bottlenecks at 10x scale' },
        { title: 'Technology Stack Health', desc: 'Analyze tech debt per technology' },
        { title: 'Cross-Service Dependencies', desc: 'Map and score service interactions' },
        { title: 'Performance Hotspots', desc: 'Identify architectural bottlenecks' },
        { title: 'Security Architecture Review', desc: 'Analyze security patterns usage' },
        { title: 'Event-Driven Readiness', desc: 'Score event sourcing implementation' }
      ],
      artemisIntelligence: [
        { title: 'Architecture Evolution', desc: 'Generate migration paths to target architecture' },
        { title: 'Pattern Implementation', desc: 'Generate pattern-based solutions' },
        { title: 'Service Decomposition', desc: 'AI-driven microservice extraction' },
        { title: 'API Design Generation', desc: 'Create consistent, versioned APIs' },
        { title: 'Database Optimization', desc: 'Evolve schema and query patterns' },
        { title: 'Caching Strategy', desc: 'Generate multi-layer caching solutions' },
        { title: 'Event Sourcing Migration', desc: 'Convert CRUD to event-sourced' },
        { title: 'Resilience Patterns', desc: 'Add circuit breakers, retries, fallbacks' },
        { title: 'Performance Architecture', desc: 'Design for specific latency targets' },
        { title: 'Domain Model Evolution', desc: 'Refactor towards DDD principles' }
      ]
    },
    security: {
      title: '🔒 Security Engineer / AppSec',
      icon: '🛡️',
      color: '#FF6B6B',
      repositoryAnalysis: [
        { title: 'Vulnerability Deep Scan', desc: 'Beyond SAST - understand attack vectors' },
        { title: 'Authentication Analysis', desc: 'Score auth implementation strength' },
        { title: 'Data Flow Tracking', desc: 'Track sensitive data through system' },
        { title: 'Encryption Usage Audit', desc: 'Verify proper encryption everywhere' },
        { title: 'Input Validation Coverage', desc: 'Find unvalidated user inputs' },
        { title: 'Access Control Review', desc: 'Analyze authorization patterns' },
        { title: 'Secret Management Audit', desc: 'Find hardcoded secrets and weak storage' },
        { title: 'Third-party Risk Assessment', desc: 'Analyze dependency vulnerabilities' },
        { title: 'Compliance Mapping', desc: 'Map code to compliance requirements' },
        { title: 'Attack Surface Analysis', desc: 'Identify and score attack vectors' }
      ],
      artemisIntelligence: [
        { title: 'Security Fix Generation', desc: 'Generate secure code alternatives' },
        { title: 'Threat Model Evolution', desc: 'Evolve comprehensive threat models' },
        { title: 'Security Test Generation', desc: 'Create penetration test scenarios' },
        { title: 'Compliance Automation', desc: 'Generate compliance-ready code' },
        { title: 'Zero Trust Implementation', desc: 'Evolve towards zero trust architecture' },
        { title: 'Encryption Strategy', desc: 'Generate end-to-end encryption' },
        { title: 'Security Monitoring', desc: 'Create security observability' },
        { title: 'Incident Response Code', desc: 'Generate incident handling logic' },
        { title: 'RBAC Implementation', desc: 'Design role-based access control' },
        { title: 'Security Training Scenarios', desc: 'Generate CTF challenges from code' }
      ]
    },
    cto: {
      title: '💼 CTO / VP Engineering',
      icon: '🎯',
      color: '#E91E63',
      repositoryAnalysis: [
        { title: 'Portfolio Health Score', desc: 'Rate entire technology portfolio' },
        { title: 'Innovation vs Maintenance', desc: 'Balance of new features vs tech debt' },
        { title: 'Team Velocity Analysis', desc: 'Code quality impact on delivery speed' },
        { title: 'Technology Risk Matrix', desc: 'Identify high-risk technology choices' },
        { title: 'Talent Utilization', desc: 'Match developer skills to code complexity' },
        { title: 'M&A Due Diligence', desc: 'Assess acquisition target code quality' },
        { title: 'Competitive Analysis', desc: 'Compare codebase to industry standards' },
        { title: 'ROI Tracking', desc: 'Measure engineering investment returns' },
        { title: 'Strategic Alignment', desc: 'Code evolution vs business goals' },
        { title: 'Future-Proofing Score', desc: 'Assess readiness for emerging tech' }
      ],
      artemisIntelligence: [
        { title: 'Technology Strategy', desc: 'AI-generated tech roadmaps' },
        { title: 'Cost Optimization Strategy', desc: 'Reduce cloud costs by 30-50%' },
        { title: 'Talent Development Plans', desc: 'Personalized growth paths from code' },
        { title: 'Innovation Opportunities', desc: 'AI identifies breakthrough possibilities' },
        { title: 'Risk Mitigation Plans', desc: 'Proactive risk reduction strategies' },
        { title: 'Modernization Roadmaps', desc: 'Phased legacy transformation plans' },
        { title: 'Acquisition Integration', desc: 'Merge codebases optimally' },
        { title: 'Platform Strategy', desc: 'Build vs buy recommendations' },
        { title: 'AI Integration Strategy', desc: 'Embed AI throughout products' },
        { title: 'Executive Dashboards', desc: 'Real-time engineering metrics' }
      ]
    }
  };

  const CategoryButton = ({ category, data }) => (
    <button
      onClick={() => setSelectedCategory(category)}
      style={{
        background: selectedCategory === category 
          ? 'white'
          : '#f8f9fa',
        border: `2px solid ${selectedCategory === category ? data.color : '#e0e0e0'}`,
        borderRadius: '12px',
        padding: '15px 20px',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        width: '100%',
        textAlign: 'left',
        boxShadow: selectedCategory === category ? '0 4px 15px rgba(0,0,0,0.1)' : 'none'
      }}
    >
      <span style={{ fontSize: '24px' }}>{data.icon}</span>
      <div>
        <div style={{ 
          fontSize: '16px', 
          fontWeight: '600',
          color: selectedCategory === category ? data.color : '#333'
        }}>
          {data.title}
        </div>
        <div style={{ 
          fontSize: '12px', 
          color: '#666',
          marginTop: '2px'
        }}>
          {data.repositoryAnalysis.length + data.artemisIntelligence.length} use cases
        </div>
      </div>
    </button>
  );

  const selectedData = useCaseCategories[selectedCategory];

  return (
    <div style={{ 
      minHeight: '100vh',
      background: '#f8f9fa',
      padding: '40px 20px'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        <h1 style={{ 
          fontSize: '36px', 
          fontWeight: '700',
          color: '#1a1a1a',
          marginBottom: '10px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          Artemis Use Cases
        </h1>
        <p style={{ 
          fontSize: '18px', 
          color: '#666', 
          lineHeight: '1.6',
          marginBottom: '40px'
        }}>
          Comprehensive capabilities for every role in your engineering organization
        </p>
      
      {/* Category Selection */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '15px',
        marginBottom: '40px'
      }}>
        {Object.entries(useCaseCategories).map(([key, data]) => (
          <CategoryButton key={key} category={key} data={data} />
        ))}
      </div>

      {/* Selected Category Use Cases */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '40px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ 
          fontSize: '28px', 
          marginBottom: '30px',
          color: '#333',
          display: 'flex',
          alignItems: 'center',
          gap: '15px'
        }}>
          <span>{selectedData.icon}</span>
          {selectedData.title}
        </h2>

        {/* Repository Analysis Section */}
        <div style={{ marginBottom: '40px' }}>
          <h3 style={{ 
            fontSize: '20px', 
            marginBottom: '20px',
            color: '#333',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            🔍 Repository Analysis & Scoring
            <span style={{
              fontSize: '14px',
              background: '#f0f0f0',
              padding: '4px 12px',
              borderRadius: '20px',
              fontWeight: 'normal',
              color: '#666'
            }}>
              Analyze, Score, Optimize
            </span>
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '15px'
          }}>
            {selectedData.repositoryAnalysis.map((useCase, index) => (
              <div key={index} style={{
                background: '#f8f9fa',
                padding: '15px 20px',
                borderRadius: '8px',
                border: '1px solid #e0e0e0',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}>
                <h4 style={{ 
                  margin: '0 0 5px 0', 
                  fontSize: '16px',
                  color: '#667eea'
                }}>
                  {useCase.title}
                </h4>
                <p style={{ 
                  margin: 0, 
                  fontSize: '13px', 
                  color: '#666',
                  lineHeight: '1.4'
                }}>
                  {useCase.desc}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Artemis Intelligence Section */}
        <div>
          <h3 style={{ 
            fontSize: '20px', 
            marginBottom: '20px',
            color: '#333',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            🧬 Artemis Intelligence
            <span style={{
              fontSize: '14px',
              background: '#f0f0f0',
              padding: '4px 12px',
              borderRadius: '20px',
              fontWeight: 'normal',
              color: '#666'
            }}>
              AI Evolution & Generation
            </span>
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '15px'
          }}>
            {selectedData.artemisIntelligence.map((useCase, index) => (
              <div key={index} style={{
                background: '#f8f9fa',
                padding: '15px 20px',
                borderRadius: '8px',
                border: '1px solid #e0e0e0',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}>
                <h4 style={{ 
                  margin: '0 0 5px 0', 
                  fontSize: '16px',
                  color: '#764ba2'
                }}>
                  {useCase.title}
                </h4>
                <p style={{ 
                  margin: 0, 
                  fontSize: '13px', 
                  color: '#666',
                  lineHeight: '1.4'
                }}>
                  {useCase.desc}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Summary Stats */}
        <div style={{
          marginTop: '40px',
          padding: '20px',
          background: '#f8f9fa',
          borderRadius: '12px',
          display: 'flex',
          justifyContent: 'space-around',
          flexWrap: 'wrap',
          gap: '20px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '32px', fontWeight: '700', color: '#667eea' }}>
              {Object.values(useCaseCategories).reduce((sum, cat) => 
                sum + cat.repositoryAnalysis.length + cat.artemisIntelligence.length, 0
              )}
            </div>
            <div style={{ fontSize: '14px', color: '#666' }}>Total Use Cases</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '32px', fontWeight: '700', color: '#667eea' }}>
              {Object.values(useCaseCategories).reduce((sum, cat) => 
                sum + cat.repositoryAnalysis.length, 0
              )}
            </div>
            <div style={{ fontSize: '14px', color: '#666' }}>Repository Analysis</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '32px', fontWeight: '700', color: '#764ba2' }}>
              {Object.values(useCaseCategories).reduce((sum, cat) => 
                sum + cat.artemisIntelligence.length, 0
              )}
            </div>
            <div style={{ fontSize: '14px', color: '#666' }}>AI Intelligence</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '32px', fontWeight: '700', color: '#667eea' }}>
              {Object.keys(useCaseCategories).length}
            </div>
            <div style={{ fontSize: '14px', color: '#666' }}>User Personas</div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default ArtemisUseCases; 